import type { SeoFaq } from "./jsonLd";

export type WritingPracticeDetails = {
  focus: string;
  prompt: string;
  corrections: string[];
  examples: Array<{
    label: string;
    learner: string;
    repair: string;
    note: string;
  }>;
  faqs: SeoFaq[];
};

const languageWritingDetails: Record<string, WritingPracticeDetails> = {
  Bengali: {
    focus: "Bangla sentence order, postpositions, verb endings, and natural everyday phrasing",
    prompt: "Write five short Bangla sentences about your family, food, study, or daily routine.",
    corrections: ["verb endings", "postpositions", "natural phrasing"],
    examples: [
      {
        label: "Natural phrasing repair",
        learner: "Ami school jabo kal.",
        repair: "Ami kal school-e jabo.",
        note: "Time words and location markers often need a more natural Bangla order.",
      },
    ],
    faqs: [
      {
        question: "How should I practice Bengali writing online?",
        answer:
          "Start with short Bangla sentences about routine topics, then repair one postposition, verb ending, or word-order issue at a time before moving into longer notes.",
      },
      {
        question: "Can Bengali writing practice help with speaking?",
        answer:
          "Yes. A corrected sentence gives you a reliable pattern to say out loud in a speaking-practice session.",
      },
    ],
  },
  French: {
    focus: "gender agreement, articles, accents, and verb forms inside full sentences",
    prompt: "Write three sentences about what you did yesterday and one thing you plan to do next.",
    corrections: ["gender agreement", "partitive articles", "accent marks"],
    examples: [
      {
        label: "Article repair",
        learner: "Je bois caf\u00e9 chaque matin.",
        repair: "Je bois du caf\u00e9 tous les matins.",
        note: "French often needs the right article before a noun, even when English does not.",
      },
    ],
    faqs: [
      {
        question: "What should I practice first in French writing?",
        answer:
          "Start with short sentences that force articles, gender agreement, accents, and common verb forms. Then rewrite the corrected sentence aloud so the pattern transfers into speaking.",
      },
      {
        question: "Can ChickyTutor correct French grammar mistakes?",
        answer:
          "Yes. Use the writing tool for sentence-level repairs, then open French speaking practice to repeat the corrected phrase in a live conversation.",
      },
    ],
  },
  Japanese: {
    focus: "particles, polite forms, sentence endings, and kana or kanji choice in short answers",
    prompt: "Write five Japanese sentences about tomorrow's plans, food, travel, or study.",
    corrections: ["particles", "polite forms", "sentence endings"],
    examples: [
      {
        label: "Particle repair",
        learner: "Ashita sushi tabetai desu.",
        repair: "Ashita sushi o tabetai desu.",
        note: "Japanese sentence practice should make particles visible before the phrase becomes a speaking habit.",
      },
    ],
    faqs: [
      {
        question: "What should I practice first in Japanese writing?",
        answer:
          "Use short sentences that force particles, polite forms, and simple verb endings. Then reuse the corrected sentence in a spoken answer.",
      },
      {
        question: "Can ChickyTutor correct Japanese sentence mistakes?",
        answer:
          "Yes. Use the writing page to repair a sentence, then move to Japanese speaking practice to say the repaired version out loud.",
      },
    ],
  },
  Korean: {
    focus: "Hangul spelling, particles, speech levels, and natural sentence endings",
    prompt: "Write a short Korean self-introduction and three sentences about your day.",
    corrections: ["particles", "speech levels", "sentence endings"],
    examples: [
      {
        label: "Particle repair",
        learner: "Oneul nalssi aju jota.",
        repair: "Oneul nalssi-ga aju joayo.",
        note: "Korean writing practice should connect particles and speech level to a sentence you can say.",
      },
    ],
    faqs: [
      {
        question: "How do I practice Korean writing after learning Hangul?",
        answer:
          "Write short daily-life sentences, check particles and speech levels, then repeat the corrected version in speaking practice.",
      },
      {
        question: "Can AI help with Korean particles?",
        answer:
          "ChickyTutor can flag likely particle and sentence-ending issues so you can practice a corrected sentence instead of memorizing rules in isolation.",
      },
    ],
  },
  Malayalam: {
    focus: "sentence order, verb endings, postpositions, and natural everyday wording",
    prompt: "Write a short note about home, work, family, or food in Malayalam.",
    corrections: ["verb endings", "postpositions", "word order"],
    examples: [
      {
        label: "Sentence flow repair",
        learner: "Njan innale market poyi pal vangi.",
        repair: "Njan innale market-il poyi pal vangi.",
        note: "Malayalam location phrases often need the right marker for the sentence to feel complete.",
      },
    ],
    faqs: [
      {
        question: "What makes Malayalam writing practice difficult?",
        answer:
          "Learners often need help with sentence order, location markers, and verb endings. Short corrected sentences are easier to reuse than long paragraphs.",
      },
      {
        question: "Can I use Malayalam writing practice before speaking?",
        answer:
          "Yes. Build one corrected sentence first, then say it out loud in Malayalam speaking practice so the pattern becomes usable.",
      },
    ],
  },
  Marathi: {
    focus: "natural sentence order, postpositions, verb endings, and everyday phrasing",
    prompt: "Write a short message introducing yourself and describing your daily routine.",
    corrections: ["verb endings", "postpositions", "natural phrasing"],
    examples: [
      {
        label: "Postposition repair",
        learner: "Mi Pune rahato.",
        repair: "Mi Puneyat rahato.",
        note: "A location usually needs the right postposition or case-marked form.",
      },
    ],
    faqs: [
      {
        question: "How do I practice Marathi writing as a beginner?",
        answer:
          "Write short self-introductions, routine sentences, and messages that use postpositions. Focus on one verb ending or location phrase at a time before expanding the paragraph.",
      },
      {
        question: "Can I use Marathi writing practice before speaking?",
        answer:
          "Yes. Use corrections to build a sentence you trust, then say the same sentence in a ChickyTutor speaking session so it becomes usable in conversation.",
      },
    ],
  },
  Nepali: {
    focus: "sentence order, particles, respectful forms, and practical everyday expressions",
    prompt: "Write a short note about your family, work, or study routine in Nepali.",
    corrections: ["particles", "respectful forms", "word order"],
    examples: [
      {
        label: "Respectful form",
        learner: "Tapai kaha bascha?",
        repair: "Tapai kaha basnu huncha?",
        note: "Respectful phrasing matters in everyday Nepali questions.",
      },
    ],
    faqs: [
      {
        question: "What makes Nepali writing practice hard?",
        answer:
          "Learners often need help with respectful forms, particles, and natural sentence order. Short corrected notes are easier to reuse than long paragraphs.",
      },
      {
        question: "Does ChickyTutor help with Nepali sentence order?",
        answer:
          "Yes. Write a short Nepali answer, check the corrected order, then practice saying the repaired answer out loud.",
      },
    ],
  },
  Polish: {
    focus: "cases, endings, word order, and consonant-heavy sentence flow",
    prompt: "Write five simple sentences about shopping, family, or your daily routine.",
    corrections: ["case endings", "gender agreement", "verb aspect"],
    examples: [
      {
        label: "Case ending repair",
        learner: "Ide do sklep kupic mleko.",
        repair: "Ide do sklepu kupic mleko.",
        note: "Polish prepositions often trigger case changes, including common location phrases.",
      },
    ],
    faqs: [
      {
        question: "How do I practice Polish cases in writing?",
        answer:
          "Write short sentences around one preposition or daily scenario, then check the case ending. Repeating corrected sentences aloud helps case endings feel less abstract.",
      },
      {
        question: "Can AI correct Polish case endings?",
        answer:
          "ChickyTutor can flag likely case-ending and word-order issues, then you can move the corrected sentence into a speaking-practice drill.",
      },
    ],
  },
  Swahili: {
    focus: "noun classes, agreement, verb forms, and natural everyday sentence patterns",
    prompt: "Write a short self-introduction and describe what you do in the morning.",
    corrections: ["noun class agreement", "verb tense markers", "subject prefixes"],
    examples: [
      {
        label: "Agreement repair",
        learner: "Mimi ni mwanafunzi mzuri na napenda kusoma kitabu nzuri.",
        repair: "Mimi ni mwanafunzi mzuri na napenda kusoma kitabu kizuri.",
        note: "Swahili adjectives need to agree with the noun class, so kitabu takes kizuri.",
      },
    ],
    faqs: [
      {
        question: "How do I practice writing in Swahili?",
        answer:
          "Start with self-introductions, morning routines, and short descriptions that use noun classes. Check agreement, verb tense markers, and subject prefixes before writing longer paragraphs.",
      },
      {
        question: "Can ChickyTutor help with Swahili noun classes?",
        answer:
          "Yes. Use the writing page to repair noun-class agreement, then repeat the corrected sentence in a speaking session so the pattern becomes automatic.",
      },
    ],
  },
};

export function getWritingPracticeDetails(languageName: string): WritingPracticeDetails {
  return (
    languageWritingDetails[languageName] ?? {
      focus: "grammar, vocabulary, natural phrasing, and sentence-level clarity",
      prompt: `Write a short self-introduction in ${languageName}, then add three sentences about your daily routine.`,
      corrections: ["grammar patterns", "natural phrasing", "sentence clarity"],
      examples: [
        {
          label: "Sentence repair",
          learner: `I write a short ${languageName} sentence with one mistake.`,
          repair: `Rewrite the sentence with clearer ${languageName} grammar and phrasing.`,
          note: "Use the correction as a pattern, then write one new sentence with the same structure.",
        },
      ],
      faqs: [
        {
          question: `How should I practice ${languageName} writing?`,
          answer: `Start with short ${languageName} sentences, ask for one correction at a time, and reuse the corrected pattern in a new sentence.`,
        },
        {
          question: `Can writing practice help me speak ${languageName}?`,
          answer: `Yes. Writing gives you a corrected sentence to trust, and speaking practice helps you turn that sentence into a real answer.`,
        },
      ],
    }
  );
}
